import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import WeatherPulseNav from "./WeatherPulseNav";
import ListenTranslateBar from "@/components/ListenTranslateBar";
import PageGuide from "@/components/PageGuide";
import { ArrowRight, BookOpen, Sparkle } from "@phosphor-icons/react";
import GuideListenButton from "@/components/GuideListenButton";

/** Learn Weather — curriculum hub. Each topic card carries a short honest
 * excerpt plus a Wikipedia reference link (per the site's sourcing pattern):
 * excerpts are our own summaries; Wikipedia is the further-reading pointer.
 * Every topic also has a full AI deep-dive guide, generated on first open. */

const TOPIC_SLUGS = {
  "Temperature": "temperature", "Humidity": "humidity", "Air pressure": "air-pressure",
  "Wind": "wind", "Clouds": "clouds", "How rain forms": "rain", "Rain gauges": "rain-gauges",
  "Thunderstorms": "thunderstorms", "Lightning": "lightning", "Hail": "hail", "Monsoon": "monsoon",
};

function LearnDeepDive({ title, testid }) {
  const slug = TOPIC_SLUGS[title];
  const [open, setOpen] = useState(false);
  const [guide, setGuide] = useState(null);
  if (!slug) return null;
  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !guide) {
      http.get(`/weather/learn/${slug}/guide`).then(({ data }) => setGuide(data.guide)).catch(() => {});
    }
  };
  return (
    <div className="mt-3">
      <button onClick={toggle} data-testid={`${testid}-deepdive-btn`}
        className="inline-flex items-center gap-1.5 rounded-full border border-[#0C4A6E]/25 bg-white px-3.5 py-1.5 font-mono-tech text-[9px] tracking-[0.2em] uppercase text-[#0C4A6E] hover:bg-[#F0F9FF] transition-colors">
        <Sparkle size={11} weight="fill" /> {open ? "Hide full guide" : "Full AI guide"}
      </button>
      {open && (
        <div className="mt-3 rounded-xl border border-[#BAE6FD] bg-[#F0F9FF] p-4" data-testid={`${testid}-deepdive`}>
          {!guide ? (
            <div className="text-sm text-[#6B7280] animate-pulse">Writing your guide…</div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[#0369A1]">Deep dive</div>
                <GuideListenButton color="#0369A1" testid={`${testid}-listen`}
                  sections={[
                    { title: title, text: guide.intro },
                    { title: "The science", text: guide.explanation },
                    { title: "Try this at home", text: guide.try_this },
                    { title: "Did you know", text: (guide.did_you_know || []).join(". ") },
                  ].filter((s) => s.text)} />
              </div>
              <p className="mt-2 text-sm text-[#0C2D48] leading-relaxed font-medium" data-testid={`${testid}-dd-intro`}>{guide.intro}</p>
              <p className="mt-2 text-sm text-[#3A3830] leading-relaxed whitespace-pre-line" data-testid={`${testid}-dd-explanation`}>{guide.explanation}</p>
              {guide.try_this && (
                <div className="mt-3 rounded-lg bg-white/80 border border-[#BAE6FD] p-3">
                  <div className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[#0369A1]">Try this at home</div>
                  <p className="mt-1 text-sm text-[#3A3830] leading-relaxed" data-testid={`${testid}-dd-try`}>{guide.try_this}</p>
                </div>
              )}
              {guide.did_you_know?.length > 0 && (
                <ul className="mt-2.5 space-y-1">
                  {guide.did_you_know.map((f, i) => (
                    <li key={i} className="text-sm text-[#3A3830]" data-testid={`${testid}-dd-fact-${i}`}>• {f}</li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
const TOPICS = [
  {
    group: "Weather basics",
    items: [
      { title: "Temperature", excerpt: "Temperature measures how hot or cold the air is — physically, the average kinetic energy of air molecules. It's measured with thermometers and reported in °C (SI) or °F.", wiki: "https://en.wikipedia.org/wiki/Temperature" },
      { title: "Humidity", excerpt: "Humidity is the amount of water vapour in the air. Relative humidity compares current vapour to the maximum the air can hold at that temperature — 100% means saturation, fog or rain.", wiki: "https://en.wikipedia.org/wiki/Humidity" },
      { title: "Air pressure", excerpt: "Atmospheric pressure is the weight of the air column above you, measured in hectopascals (hPa). Falling pressure often signals approaching storms; rising pressure usually means settling weather.", wiki: "https://en.wikipedia.org/wiki/Atmospheric_pressure" },
      { title: "Wind", excerpt: "Wind is air moving from high-pressure areas toward low-pressure areas. The bigger the pressure difference over a distance, the stronger the wind.", wiki: "https://en.wikipedia.org/wiki/Wind" },
    ],
  },
  {
    group: "Clouds & rain",
    items: [
      { title: "Clouds", excerpt: "Clouds form when moist air rises, cools, and its water vapour condenses onto tiny particles. The main families are cirrus (high, wispy), cumulus (puffy) and stratus (layered).", wiki: "https://en.wikipedia.org/wiki/Cloud" },
      { title: "How rain forms", excerpt: "Rain begins when cloud droplets collide and grow heavy enough to fall. In cold clouds, ice crystals grow instead and melt on the way down — the Bergeron process.", wiki: "https://en.wikipedia.org/wiki/Rain" },
      { title: "Rain gauges", excerpt: "A rain gauge is a simple cylinder that collects rainfall over a known area so it can be measured in millimetres. One millimetre of rain equals one litre of water per square metre.", wiki: "https://en.wikipedia.org/wiki/Rain_gauge" },
    ],
  },
  {
    group: "Storms",
    items: [
      { title: "Thunderstorms", excerpt: "Thunderstorms are cumulonimbus clouds powered by strong convection — warm moist air rising fast. They produce lightning, thunder, heavy rain, gusty winds and sometimes hail.", wiki: "https://en.wikipedia.org/wiki/Thunderstorm" },
      { title: "Lightning", excerpt: "Lightning is a giant electrical discharge between cloud and ground (or within a cloud), heating air to ~30,000 °C. The rapid expansion of that air is the thunder you hear.", wiki: "https://en.wikipedia.org/wiki/Lightning" },
      { title: "Hail", excerpt: "Hail forms when strong thunderstorm updrafts carry raindrops upward into freezing air, adding layer upon layer of ice until the stone is too heavy to stay aloft.", wiki: "https://en.wikipedia.org/wiki/Hail" },
      { title: "Monsoon", excerpt: "A monsoon is a seasonal reversal of winds bringing a wet and a dry season, driven by land heating faster than the ocean. South Asia's summer monsoon delivers most of the region's annual rain.", wiki: "https://en.wikipedia.org/wiki/Monsoon" },
    ],
  },
];

const SAFETY = "Safety first: never approach tornadoes, lightning, floodwater or storm damage to make an observation. Report from a safe place, and follow your national weather service for official warnings.";

export default function WeatherLearn() {
  const [guides, setGuides] = useState([]);
  useEffect(() => {
    http.get("/cms/pages", { params: { page_type: "articles", tag: "weather-pulse", limit: 12 } })
      .then(({ data }) => setGuides(data.pages || [])).catch(() => {});
  }, []);
  return (
    <main className="min-h-screen pt-32 md:pt-36 pb-24 bg-[color:var(--np-bg,#F5EBDC)] text-[#1B4332]" data-testid="wp-learn-page">
      <div className="max-w-[1100px] mx-auto px-5 md:px-10">
        <WeatherPulseNav />
        <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-2">/ Learn to observe</div>
        <h1 className="font-serif-display text-4xl md:text-5xl tracking-tight">Understand the sky above you.</h1>
        <p className="mt-2 text-sm md:text-base text-[#3A3830] max-w-2xl">
          Short, honest explainers for every observer. Each topic starts simple and links onward to full references — full multi-level guides are being written on this foundation.
        </p>
        <div className="mt-5"><PageGuide pageKey="weather:learn" testidPrefix="wp-learn" /></div>

        <div className="mt-8 space-y-8">
          {TOPICS.map((g) => (
            <section key={g.group}>
              <h2 className="font-serif-display text-2xl md:text-3xl mb-3 flex items-center gap-2">
                <BookOpen size={20} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" /> {g.group}
              </h2>
              <div className="grid gap-3 md:grid-cols-2">
                {g.items.map((t) => (
                  <article key={t.title} data-testid={`wp-learn-${t.title.toLowerCase().replace(/\s+/g, "-")}`} className="rounded-2xl border border-black/10 bg-white p-5">
                    <h3 className="font-serif-display text-xl">{t.title}</h3>
                    <p className="mt-1.5 text-sm text-[#3A3830] leading-relaxed">{t.excerpt}</p>
                    <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
                      <a
                        href={t.wiki}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`wp-learn-wiki-${t.title.toLowerCase().replace(/\s+/g, "-")}`}
                        className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[color:var(--np-brand,#B85F2A)] hover:underline"
                      >
                        Read more on Wikipedia ↗
                      </a>
                      <ListenTranslateBar text={`${t.title}. ${t.excerpt}`} testidPrefix={`wp-learn-tools-${t.title.toLowerCase().replace(/\s+/g, "-")}`} />
                    </div>
                    <LearnDeepDive title={t.title} testid={`wp-learn-dd-${t.title.toLowerCase().replace(/\s+/g, "-")}`} />
                  </article>
                ))}
              </div>
            </section>
          ))}
        </div>

        {/* Full guides from the Weather Pulse desk */}
        {guides.length > 0 && (
          <div className="mt-10" data-testid="wp-learn-guides">
            <h2 className="font-serif-display text-2xl md:text-3xl mb-3 flex items-center gap-2">
              <BookOpen size={20} weight="fill" className="text-[#0C4A6E]" /> Guides from the Weather Pulse desk
            </h2>
            <div className="grid gap-3 md:grid-cols-2">
              {guides.map((g) => (
                <Link key={g.slug} to={`/read/articles/${g.slug}`} data-testid={`wp-learn-guide-${g.slug}`}
                  className="group rounded-2xl border border-black/10 bg-white p-5 hover:border-[#0C4A6E] hover:-translate-y-0.5 transition-all">
                  <div className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[#0C4A6E]">{g.category || "Guide"}</div>
                  <div className="mt-1 font-serif-display text-xl leading-tight">{g.title}</div>
                  <p className="mt-1.5 text-sm text-[#3A3830] line-clamp-2">{g.excerpt || g.subtitle}</p>
                  <div className="mt-2 inline-flex items-center gap-1 font-mono-tech text-[9px] tracking-[0.2em] uppercase text-[#0C4A6E]">
                    Read the guide <ArrowRight size={10} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="mt-8 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="wp-learn-safety">
          {SAFETY}
        </div>
      </div>
    </main>
  );
}
